import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Flame, ShoppingBag, Package, TrendingUp, Truck, ShieldCheck,
  ChevronRight, Crown, Lightbulb, Star,
} from "lucide-react";
import { http } from "../lib/api";
import { ProductImage } from "../components/ProductImage";
import { TestimonialCard } from "../components/TestimonialCard";
import { FeaturedReels } from "../components/FeaturedReels";
import { WaitlistForm } from "../components/WaitlistForm";
import { useConfig } from "../contexts/ConfigContext";

export default function Home() {
  const [products, setProducts] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const { purchases_enabled } = useConfig();
  useEffect(() => {
    http.get("/products").then(({ data }) => setProducts(data.products));
    http.get("/reviews").then(({ data }) => setReviews(data.reviews)).catch(() => {});
    http.get("/testimonials").then(({ data }) => setTestimonials(data.testimonials)).catch(() => {});
  }, []);
  const bestsellers = useMemo(() => products.filter((p) => p.image).slice(0, 5), [products]);

  return (
    <div className="bg-[#FFFDF8] text-[#1A1A1A]" data-testid="home-root">
      {/* Hero */}
      <section className="relative overflow-hidden border-b border-[#E5D8C1]">
        <div className="absolute inset-0 opacity-[0.08] pointer-events-none" style={{
          backgroundImage: `url("https://images.unsplash.com/photo-1702041295471-01b73fd39907?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600")`,
          backgroundSize: "cover", backgroundPosition: "center"
        }}/>
        <div className="max-w-7xl mx-auto px-6 md:px-12 pt-20 md:pt-28 pb-16 md:pb-24 relative grid md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-7">
            <div className="inline-flex items-center gap-2 bg-[#1A1A1A] text-[#FFB300] px-4 py-2 text-xs tracking-[0.3em] uppercase mb-6" data-testid="hero-badge">
              <Flame size={14} /> 100% Mexican spices · Family-run
            </div>
            <Link to="/shop" className="inline-flex items-center gap-2 bg-[#FFB300] text-[#1A1A1A] hover:bg-white px-4 py-2 text-xs tracking-[0.3em] uppercase mb-8 ml-2 font-heading transition-colors" data-testid="hero-promo">
              🎁 Buy 3, get 1 FREE
            </Link>
            <h1 className="font-heading text-[52px] leading-[0.9] sm:text-7xl md:text-8xl uppercase tracking-tight">
              Chamoy candy<br/>
              <span className="text-[#D32F2F]">with real</span><br/>
              Mexican fuego.
            </h1>
            <p className="mt-8 text-lg text-[#5C5A56] max-w-xl leading-relaxed">
              Alekas Treats is a family-run candy house crafting chamoy ropes, gummies, rim pastes and more with 100% authentic Mexican spices. Shop direct or apply for wholesale — we ship to shoppers and shop owners across the U.S.
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link to="/shop" className="bg-[#D32F2F] text-white hover:bg-[#B71C1C] px-8 py-4 uppercase tracking-widest font-heading text-lg transition-transform hover:-translate-y-1 inline-flex items-center gap-2" data-testid="hero-cta-shop">
                Shop the treats <ChevronRight size={18} />
              </Link>
              <Link to="/wholesale" className="border-2 border-[#1A1A1A] text-[#1A1A1A] hover:bg-[#1A1A1A] hover:text-[#FFFDF8] px-8 py-4 uppercase tracking-widest font-heading text-lg transition-all" data-testid="hero-cta-wholesale">
                Wholesale inquiry
              </Link>
            </div>
          </div>
          <div className="md:col-span-5 relative">
            <div className="aspect-square bg-[#FFB300] flex items-center justify-center relative">
              <img src="/products/ropes.png" alt="Extreme Ropes" className="w-[90%] h-[90%] object-contain drop-shadow-2xl animate-float" />
            </div>
            <div className="absolute -bottom-6 -left-6 bg-[#D32F2F] text-white p-5 max-w-[220px] shadow-xl">
              <div className="font-heading text-3xl tracking-wider">VIRAL</div>
              <div className="text-xs tracking-widest uppercase mt-1">on TikTok · 100% Mexican spices</div>
            </div>
          </div>
        </div>
      </section>

      {/* Reels / TikTok — flagship marketing section right after the hero */}
      <FeaturedReels reviews={reviews} />

      {/* Coming Soon waitlist — only when online purchases are gated */}
      {!purchases_enabled && (
        <section className="max-w-7xl mx-auto px-6 md:px-12 py-16" data-testid="home-waitlist">
          <div className="grid md:grid-cols-2 gap-10 items-center">
            <div>
              <div className="inline-flex items-center gap-2 bg-[#FFB300] text-[#1A1A1A] px-3 py-1.5 text-[10px] tracking-[0.3em] uppercase font-heading">
                Launching soon
              </div>
              <h2 className="font-heading text-5xl md:text-6xl uppercase tracking-tight leading-[0.95] mt-4">
                Online orders are<br /><span className="text-[#D32F2F]">days away.</span>
              </h2>
              <p className="text-lg text-[#5C5A56] mt-4 leading-relaxed">
                We&apos;re packing our shipping room right now. Drop your email — you&apos;ll be the first to know the second we open, plus a <b>founding-fan promo code</b> as a thank-you.
              </p>
              <p className="text-sm text-[#5C5A56] mt-4">
                In the meantime, DM us at <a href="https://www.tiktok.com/@alekastreatsllc" target="_blank" rel="noreferrer" className="text-[#D32F2F] font-semibold">@alekastreatsllc</a> on TikTok or Instagram to place an order today.
              </p>
            </div>
            <WaitlistForm interest="retail" headline="Get on the list" subhead="One email, no spam." />
          </div>
        </section>
      )}

      {/* Two paths */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 py-16 grid md:grid-cols-2 gap-8" data-testid="home-paths">
        <Link to="/shop" className="group bg-[#FDF4E3] border border-[#E5D8C1] p-10 hover:border-[#D32F2F] transition-colors relative overflow-hidden" data-testid="path-shop">
          <ShoppingBag className="text-[#D32F2F]" size={32} />
          <h3 className="font-heading text-3xl md:text-4xl uppercase tracking-wide mt-6 leading-tight">For candy lovers</h3>
          <p className="text-[#5C5A56] mt-3 leading-relaxed">Order chilito candy for yourself. From $6 · fast US shipping.</p>
          <div className="mt-6 inline-flex items-center gap-2 text-sm uppercase tracking-widest font-heading text-[#1A1A1A] group-hover:text-[#D32F2F]">Shop retail <ChevronRight size={16} /></div>
        </Link>
        <Link to="/wholesale" className="group bg-[#1A1A1A] text-[#FFFDF8] p-10 hover:bg-[#D32F2F] transition-colors relative overflow-hidden" data-testid="path-wholesale">
          <Package className="text-[#FFB300] group-hover:text-white" size={32} />
          <h3 className="font-heading text-3xl md:text-4xl uppercase tracking-wide mt-6 leading-tight">For shop owners</h3>
          <p className="text-white/70 mt-3 leading-relaxed">Stock your store. Cases from $36 · 50% retail margin · Approval in 1-2 days.</p>
          <div className="mt-6 inline-flex items-center gap-2 text-sm uppercase tracking-widest font-heading text-[#FFB300] group-hover:text-white">Apply for wholesale <ChevronRight size={16} /></div>
        </Link>
      </section>

      {/* Club + Request */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 pb-16 grid md:grid-cols-2 gap-8" data-testid="home-extras">
        <Link to="/club" className="group bg-gradient-to-br from-[#1A1A1A] via-[#B71C1C] to-[#D32F2F] text-white p-10 relative overflow-hidden transition-transform hover:-translate-y-1" data-testid="path-club">
          <div className="absolute top-4 right-4 bg-[#FFB300] text-[#1A1A1A] text-[10px] uppercase tracking-widest px-2 py-1 font-heading">$20/mo</div>
          <Crown className="text-[#FFB300]" size={32} />
          <h3 className="font-heading text-3xl md:text-4xl uppercase tracking-wide mt-6 leading-tight">Chamoy Club</h3>
          <p className="text-white/80 mt-3 leading-relaxed">4 chilito candies of your choice every month · early access to new flavors · exclusive drops.</p>
          <div className="mt-6 inline-flex items-center gap-2 text-sm uppercase tracking-widest font-heading text-[#FFB300] group-hover:text-white">Join the club <ChevronRight size={16} /></div>
        </Link>
        <Link to="/request" className="group bg-[#FFB300] p-10 relative overflow-hidden transition-transform hover:-translate-y-1" data-testid="path-request">
          <Lightbulb className="text-[#1A1A1A]" size={32} />
          <h3 className="font-heading text-3xl md:text-4xl uppercase tracking-wide mt-6 leading-tight text-[#1A1A1A]">Request a candy</h3>
          <p className="text-[#1A1A1A]/80 mt-3 leading-relaxed">Got a chamoy flavor idea? Drop it — the ones we hear most often become real chilito candies.</p>
          <div className="mt-6 inline-flex items-center gap-2 text-sm uppercase tracking-widest font-heading text-[#1A1A1A]">Suggest a flavor <ChevronRight size={16} /></div>
        </Link>
      </section>

      {/* Products */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 py-16" data-testid="home-catalog">
        <div className="flex items-end justify-between mb-14 flex-wrap gap-4">
          <div>
            <div className="text-xs tracking-[0.3em] uppercase text-[#D32F2F] mb-3">The Lineup</div>
            <h2 className="font-heading text-5xl md:text-6xl uppercase tracking-wide">Bestsellers</h2>
          </div>
          <Link to="/shop" className="text-sm uppercase tracking-widest border-b-2 border-[#1A1A1A] pb-1 hover:text-[#D32F2F] hover:border-[#D32F2F]">All treats →</Link>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-6 md:gap-10">
          {bestsellers.map((p, i) => (
            <Link to={`/product/${p.id}`} key={p.id} className="group" data-testid={`home-product-${p.id}`}>
              <div className={`aspect-square ${i % 2 === 0 ? "bg-[#FDF4E3]" : "bg-[#FFB300]/30"} flex items-center justify-center overflow-hidden relative`}>
                <ProductImage p={p} className="group-hover:scale-105 transition-transform duration-500" />
              </div>
              <div className="mt-4">
                <div className="font-heading text-lg uppercase tracking-wide leading-tight">{p.name}</div>
                <div className="text-xs text-[#5C5A56] mt-1">{p.size_oz} oz · ${p.retail_msrp?.toFixed(2)}</div>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* Text testimonials wall */}
      {testimonials.length > 0 && (
        <section className="bg-[#FDF4E3] border-y border-[#E5D8C1]" data-testid="home-testimonials">
          <div className="max-w-7xl mx-auto px-6 md:px-12 py-20">
            <div className="flex items-end justify-between mb-10 flex-wrap gap-4">
              <div>
                <div className="text-xs tracking-[0.3em] uppercase text-[#D32F2F] mb-3">Straight from the DMs</div>
                <h2 className="font-heading text-5xl md:text-6xl uppercase tracking-wide leading-[0.95]">What people are saying</h2>
              </div>
              <div className="flex items-center gap-2 text-sm text-[#5C5A56]">
                <div className="flex text-[#FFB300]">
                  {[0, 1, 2, 3, 4].map((i) => <Star key={i} size={16} fill="currentColor" strokeWidth={0} />)}
                </div>
                <span className="uppercase tracking-widest text-xs font-heading">Loved by chamoy heads</span>
              </div>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {testimonials.slice(0, 6).map((t) => (
                <TestimonialCard key={t.id} t={t} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Wholesale benefits */}
      <section className="bg-[#1A1A1A] text-[#FFFDF8]" data-testid="home-benefits">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-24 grid md:grid-cols-3 gap-10">
          <div>
            <div className="text-[#FFB300] text-xs tracking-[0.3em] uppercase mb-3">For retailers</div>
            <h2 className="font-heading text-4xl md:text-5xl uppercase leading-tight">Move product.<br/>Not paperwork.</h2>
          </div>
          {[
            { icon: <Package />, title: "Low MOQ", body: "Start with just 1 case per SKU. Mix & match." },
            { icon: <TrendingUp />, title: "50% margin", body: "Wholesale case at 50% off suggested retail. Fast-moving impulse SKUs." },
            { icon: <Truck />, title: "Fast US shipping", body: "Ships to any commercial address in the U.S." },
            { icon: <ShieldCheck />, title: "100% Mexican spices", body: "Every chile, tamarind and lime — sourced from real Mexican growers." },
          ].map((b) => (
            <div key={b.title} className="border-l-2 border-[#FFB300] pl-6">
              <div className="text-[#FFB300] mb-3">{b.icon}</div>
              <div className="font-heading text-xl tracking-wide uppercase">{b.title}</div>
              <div className="text-sm text-[#FDF4E3]/70 mt-2 leading-relaxed">{b.body}</div>
            </div>
          ))}
        </div>
      </section>

      {/* About */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 py-24 grid md:grid-cols-2 gap-16 items-center" data-testid="home-about">
        <div className="relative">
          <div className="aspect-[4/5] overflow-hidden">
            <img src="https://images.unsplash.com/photo-1625921133217-8d978f7872b8?crop=entropy&cs=srgb&fm=jpg&q=85&w=900" alt="Mexican spices" className="w-full h-full object-cover" />
          </div>
          <div className="absolute -bottom-6 -right-6 bg-[#FFB300] p-6 max-w-[260px]">
            <div className="font-heading text-2xl tracking-wide uppercase leading-tight">Family kitchen to gas station shelf</div>
          </div>
        </div>
        <div>
          <div className="text-xs tracking-[0.3em] uppercase text-[#D32F2F] mb-3">Our Story</div>
          <h2 className="font-heading text-5xl md:text-6xl uppercase leading-[0.95]">Real chamoy. Real spice. Real family recipe.</h2>
          <p className="text-lg text-[#5C5A56] leading-relaxed mt-6">
            Alekas Treats LLC is a family-run candy house. We source every chile, tamarind and spice from Mexican growers we know by name — no shortcuts, no filler. Our chamoy candy is crafted with 100% Mexican spices to bring you the real street-candy experience.
          </p>
          <p className="text-lg text-[#5C5A56] leading-relaxed mt-4">
            We ship direct to snack lovers, and wholesale to gas stations, vape and smoke shops, quinceañera vendors, and party stores across the U.S.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link to="/shop" className="inline-flex items-center gap-2 bg-[#D32F2F] text-white hover:bg-[#B71C1C] px-8 py-4 uppercase tracking-widest font-heading transition-colors" data-testid="about-cta-shop">
              Shop retail <ChevronRight size={18} />
            </Link>
            <Link to="/wholesale" className="inline-flex items-center gap-2 border-2 border-[#1A1A1A] text-[#1A1A1A] hover:bg-[#1A1A1A] hover:text-white px-8 py-4 uppercase tracking-widest font-heading transition-colors" data-testid="about-cta-wholesale">
              Wholesale
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
